import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { usePatchCatalogItemMutation, usePatchCatalogSongMutation } from '../../../../../../redux/api/fymAPI';
import { CatalogItem, CatalogSong } from '../../../../../../redux/api/fymAPI.types';
import { drawnFileSrc } from '../../../../../common/drawnImageSrc';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { SONG_COVER_FIELD_PX } from '../details/songCoverFieldSize';
import {
  changedFields,
  draftFromServer,
  draftPatches,
  isItemField,
  SongDraft,
  SongDraftField,
  validateDraft,
} from './songPageDraft';

/** What the save bar shows (design song-page f5). */
export type SongSaveStatus = 'idle' | 'saving' | 'saved' | 'failed' | 'partFailed';

/** How long "All changes saved" stays (f5: "The bar goes away 1 s after a save"). */
const SAVED_NOTE_MS = 1000;

/** A change of the server in this time after an own save is the echo of that save, not another device. */
const OWN_SAVE_ECHO_MS = 5000;

/** How long the note "changed on another device" stays when nothing was edited (f9). */
export const CHANGED_ELSEWHERE_NOTE_MS = 4000;

/** f9: nothing (`none`), the info line (`shown`), or the warn line while the user edits (`whileEditing`). */
export type ChangedElsewhere = 'none' | 'shown' | 'whileEditing';

interface UseSongPageDraftProps {
  accountId: number;
  item: CatalogItem;
  song: CatalogSong | undefined;
  /** The demo artist: nothing is editable, so the save bar never shows (f7). */
  readOnly: boolean;
}

/**
 * The text fields of the song page and their one save bar (owner decision 1, design b3, d2, e3, f5,
 * f9). The draft is the server value with the user's edits on top. One Save sends
 * `PATCH .../catalog/:uuid` and `PATCH .../catalog/:uuid/song`, each only when one of its fields
 * changed; the bar keeps the part that failed. A saved value counts as the server value until the
 * read after the save brings it, so the bar does not come back for a moment.
 */
export function useSongPageDraft({ accountId, item, song, readOnly }: UseSongPageDraftProps) {
  const [patchItem] = usePatchCatalogItemMutation();
  const [patchSong] = usePatchCatalogSongMutation();
  // The cover field draws the cover through imgproxy (owner rule 2026-10-19); an old API answer
  // with no `sourceUrl` falls back to the file route.
  const serverDraft = useMemo(
    () =>
      draftFromServer(item, song, (relative) =>
        item.artworkFile === null ? getFullAPIFileURL(relative) : drawnFileSrc(item.artworkFile, SONG_COVER_FIELD_PX)
      ),
    [item, song]
  );
  const [edits, setEdits] = useState<Partial<SongDraft>>({});
  const [saved, setSaved] = useState<Partial<SongDraft>>({});
  const [status, setStatus] = useState<SongSaveStatus>('idle');
  const [failure, setFailure] = useState<unknown>(undefined);
  const [changedElsewhere, setChangedElsewhere] = useState<ChangedElsewhere>('none');
  const lastSaveAt = useRef(0);
  const previousServer = useRef<SongDraft | null>(null);

  const base = useMemo(() => ({ ...serverDraft, ...saved }), [saved, serverDraft]);
  const draft = useMemo(() => ({ ...base, ...edits }), [base, edits]);
  const { kind } = item;
  const changed = useMemo(() => (readOnly ? [] : changedFields(base, draft, kind)), [base, draft, kind, readOnly]);
  const errors = useMemo(() => validateDraft(draft, kind), [draft, kind]);
  const errorCount = useMemo(() => changed.filter((field) => errors[field] !== undefined).length, [changed, errors]);

  // The server caught up: a saved value that the server now holds is the server value again.
  useEffect(() => {
    setSaved((current) => {
      const left = (Object.keys(current) as SongDraftField[]).filter(
        (field) => changedFields(serverDraft, { ...serverDraft, [field]: current[field] }, kind).length > 0
      );
      if (left.length === Object.keys(current).length) {
        return current;
      }
      return Object.fromEntries(left.map((field) => [field, current[field]])) as Partial<SongDraft>;
    });
  }, [kind, serverDraft]);

  // f9: another device changed the row or the song.
  useEffect(() => {
    const previous = previousServer.current;
    previousServer.current = serverDraft;
    if (previous === null || Date.now() - lastSaveAt.current < OWN_SAVE_ECHO_MS) {
      return;
    }
    if (changedFields(previous, serverDraft, kind).length === 0) {
      return;
    }
    setChangedElsewhere(Object.keys(edits).length > 0 ? 'whileEditing' : 'shown');
    // `edits` is read on purpose without being a dependency: only a server change starts this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, serverDraft]);

  useEffect(() => {
    if (changedElsewhere !== 'shown') {
      return undefined;
    }
    const timer = window.setTimeout(() => setChangedElsewhere('none'), CHANGED_ELSEWHERE_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [changedElsewhere]);

  useEffect(() => {
    if (status !== 'saved') {
      return undefined;
    }
    const timer = window.setTimeout(() => setStatus('idle'), SAVED_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const setField = useCallback(<K extends SongDraftField>(field: K, value: SongDraft[K]) => {
    setEdits((current) => ({ ...current, [field]: value }));
  }, []);

  const discard = useCallback(() => {
    setEdits({});
    setStatus('idle');
    setFailure(undefined);
    setChangedElsewhere('none');
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (readOnly || changed.length === 0 || errorCount > 0) {
      return changed.length === 0;
    }
    const fields = changed;
    const sent: Partial<SongDraft> = Object.fromEntries(fields.map((field) => [field, draft[field]]));
    const patches = draftPatches(fields, draft);
    setStatus('saving');
    setFailure(undefined);
    const done: SongDraftField[] = [];
    let firstFailure: unknown;
    if (patches.item !== null) {
      try {
        await patchItem({ accountId, itemUuid: item.uuid, ...patches.item }).unwrap();
        done.push(...fields.filter(isItemField));
      } catch (error) {
        firstFailure = error;
      }
    }
    if (patches.song !== null) {
      try {
        await patchSong({ accountId, itemUuid: item.uuid, ...patches.song }).unwrap();
        done.push(...fields.filter((field) => !isItemField(field)));
      } catch (error) {
        firstFailure ??= error;
      }
    }
    lastSaveAt.current = Date.now();
    if (done.length > 0) {
      setSaved((current) => ({
        ...current,
        ...Object.fromEntries(done.map((field) => [field, sent[field]])),
      }));
      setEdits((current) => {
        // A field the user changed again while the save ran keeps the new edit.
        const next = { ...current };
        done.forEach((field) => {
          if (next[field] === sent[field]) {
            delete next[field];
          }
        });
        return next;
      });
    }
    if (firstFailure === undefined) {
      setStatus('saved');
      setChangedElsewhere('none');
      return true;
    }
    setFailure(firstFailure);
    setStatus(done.length > 0 ? 'partFailed' : 'failed');
    return false;
  }, [accountId, changed, draft, errorCount, item.uuid, patchItem, patchSong, readOnly]);

  // Cmd+S / Ctrl+S saves (design b3 note).
  useEffect(() => {
    if (changed.length === 0) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        save().catch(console.error);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [changed.length, save]);

  const dismissChangedElsewhere = useCallback(() => setChangedElsewhere('none'), []);

  return {
    base,
    draft,
    setField,
    changed,
    errors,
    errorCount,
    isDirty: changed.length > 0,
    isUnreleased: kind === CatalogItemKind.unreleased,
    status,
    failure,
    save,
    discard,
    changedElsewhere,
    dismissChangedElsewhere,
  };
}

export type SongPageDraftState = ReturnType<typeof useSongPageDraft>;
